import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, LogOut, LayoutDashboard, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import api from "@/api/client";
import SearchBox from "@/components/SearchBox";
import MenuLink from "@/components/MenuLink";
import { useSiteContent } from "@/context/SiteContentContext";
import { MENU_KEYS, defaultNavLinks, parseLinks, categorySlugOf, slugify } from "@/lib/menuLinks";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { content } = useSiteContent();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [expanded, setExpanded] = useState({}); // mobile expanded subgroups

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => setCats([]));
  }, []);

  const loaded = Object.keys(content).length > 0;
  const navLinks = loaded && content[MENU_KEYS.navVisible] !== "false"
    ? parseLinks(content[MENU_KEYS.navLinks], cats.length ? defaultNavLinks(cats) : [])
    : [];
  const keyOf = (l, i) => categorySlugOf(l.url) || slugify(l.name) || String(i);
  const subsFor = (slug) =>
    cats
      .filter((c) => c.parent_slug === slug)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  return (
    <header className="sticky top-0 z-40 glass-header" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="flex items-center justify-between gap-2 sm:gap-4 min-h-16 xl:py-1">
          <div className="flex flex-1 min-w-0 items-center gap-5">
            <Link to="/" data-testid="brand-logo" className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <img src="/cityshor-logo.png" alt="CityShor" className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover" />
              <div className="leading-none whitespace-nowrap">
                <div className="font-display text-lg sm:text-xl tracking-tight text-[#111827]">CityShor</div>
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[#4B5563]">Pop-Up Bazaar</div>
              </div>
            </Link>
            {navLinks.length > 0 && (
            <nav className="hidden xl:flex flex-1 min-w-0 flex-wrap items-center justify-center border-l border-gray-200 pl-3" data-testid="nav-links">
              {navLinks.map((l, i) => {
                const catSlug = categorySlugOf(l.url);
                const subs = catSlug ? subsFor(catSlug) : [];
                const testKey = keyOf(l, i);
                return (
                  <div key={`${testKey}-${i}`} className="relative group" data-testid={`nav-item-${testKey}`}>
                    <MenuLink
                      url={l.url}
                      data-testid={`nav-${testKey}`}
                      className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-2 text-sm text-[#4B5563] hover:text-[#F48E2E] transition-colors"
                    >
                      {l.name}
                      {subs.length > 0 && <ChevronDown className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" />}
                    </MenuLink>
                    {subs.length > 0 && (
                      <div
                        data-testid={`nav-dropdown-${testKey}`}
                        className="invisible opacity-0 group-hover:visible group-hover:opacity-100 focus-within:visible focus-within:opacity-100 transition-opacity duration-150 absolute left-0 top-full pt-1 z-50"
                      >
                        <div className="min-w-[220px] rounded-xl border border-gray-200 bg-white shadow-lg py-2">
                          <Link
                            to={`/category/${catSlug}`}
                            data-testid={`nav-sub-${testKey}-all`}
                            className="block px-4 py-2 text-sm text-[#111827] hover:bg-[#FEF3E6] hover:text-[#F48E2E] transition-colors"
                          >
                            All {l.name}
                          </Link>
                          <div className="my-1 h-px bg-gray-100" />
                          {subs.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/category/${s.slug}`}
                              data-testid={`nav-sub-${s.slug}`}
                              className="block px-4 py-2 text-sm text-[#4B5563] hover:bg-[#FEF3E6] hover:text-[#F48E2E] transition-colors"
                            >
                              {s.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
            <div className="hidden md:block">
              <SearchBox variant="desktop" />
            </div>
            <Link to="/cart" data-testid="nav-cart" className="relative p-1.5 sm:p-2 rounded-full hover:bg-gray-100 transition-colors">
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-[#F48E2E] text-white text-[10px] font-bold flex items-center justify-center">{count}</span>
              )}
            </Link>

            {!user ? (
              <>
                <Button variant="ghost" data-testid="nav-login-btn" className="px-2 sm:px-4 text-sm" onClick={() => nav("/login")}>Log in</Button>
                <Button data-testid="nav-signup-btn" className="bg-[#F48E2E] hover:bg-[#D97815] text-white rounded-full px-3 sm:px-4 h-9 sm:h-10 text-sm" onClick={() => nav("/register")}>Sign up</Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" data-testid="nav-user-menu" className="rounded-full">
                    <User className="h-4 w-4 mr-1" /> {user.full_name?.split(" ")[0] || "Account"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <div className="px-2 py-1.5 text-xs text-gray-500">{user.email}</div>
                  <DropdownMenuSeparator />
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><LayoutDashboard className="h-4 w-4 mr-2" />Admin Dashboard</DropdownMenuItem>
                  )}
                  {user.role === "vendor" && (
                    <DropdownMenuItem data-testid="menu-vendor" onClick={() => nav("/vendor")}><LayoutDashboard className="h-4 w-4 mr-2" />Exhibitor Dashboard</DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="menu-my-orders" onClick={() => nav("/orders")}>My Orders</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }}>
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <button data-testid="mobile-menu-btn" className="xl:hidden p-1.5 sm:p-2" onClick={() => setOpen(!open)}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="xl:hidden py-3 border-t border-gray-200 space-y-1">
            <div className="px-2 pb-2">
              <SearchBox variant="mobile" />
            </div>
            {navLinks.map((l, i) => {
              const catSlug = categorySlugOf(l.url);
              const subs = catSlug ? subsFor(catSlug) : [];
              const k = keyOf(l, i);
              const ek = `${k}-${i}`;
              const isOpen = !!expanded[ek];
              return (
                <div key={`${k}-${i}`} data-testid={`mob-nav-group-${k}`}>
                  <div className="flex items-center">
                    <MenuLink
                      url={l.url}
                      data-testid={`mob-nav-${k}`}
                      onClick={() => setOpen(false)}
                      className="flex-1 block px-2 py-2 text-sm hover:bg-gray-50 rounded-md"
                    >
                      {l.name}
                    </MenuLink>
                    {subs.length > 0 && (
                      <button
                        data-testid={`mob-nav-toggle-${k}`}
                        onClick={() => setExpanded((e) => ({ ...e, [ek]: !e[ek] }))}
                        className="p-2 text-gray-500"
                        aria-label={`Toggle ${l.name} subcategories`}
                      >
                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {isOpen && subs.length > 0 && (
                    <div className="pl-4 pb-1">
                      {subs.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/category/${s.slug}`}
                          data-testid={`mob-nav-sub-${s.slug}`}
                          onClick={() => setOpen(false)}
                          className="block px-2 py-2 text-sm text-[#4B5563] hover:bg-gray-50 rounded-md"
                        >
                          {s.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
}
